import { Suspense, lazy } from 'react';
import { Toaster } from "@/components/ui/sonner";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CompareProvider } from "@/contexts/CompareContext";
import Layout from "@/components/layout/Layout";
import CompareBar from "@/components/compare/CompareBar";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";

// Redirect component for solution detail pages
function RedirectSolutionDetail() {
  const { id } = useParams();
  return <Navigate to={`/search/${id}`} replace />;
}

// Lazy load pages for code splitting
const Home = lazy(() => import("@/pages/Home"));
const Solutions = lazy(() => import("@/pages/Solutions"));
const SolutionDetail = lazy(() => import("@/pages/SolutionDetail"));
const Platforms = lazy(() => import("@/pages/Platforms"));
const PlatformDetail = lazy(() => import("@/pages/PlatformDetail"));
const Insights = lazy(() => import("@/pages/InsightsPage"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Compare = lazy(() => import("@/pages/Compare"));
const Auth = lazy(() => import("@/pages/Auth"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Contact = lazy(() => import("@/pages/Contact"));
const About = lazy(() => import("@/pages/About"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Vendor pages
const VendorDashboard = lazy(() => import("@/pages/vendor/Dashboard"));
const SubmitSolution = lazy(() => import("@/pages/vendor/SubmitSolution"));
const SubmitPlatform = lazy(() => import("@/pages/vendor/SubmitPlatform"));
const VendorEditSolution = lazy(() => import("@/pages/vendor/EditSolution"));
const VendorEditPlatform = lazy(() => import("@/pages/vendor/EditPlatform"));

// Admin pages
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminEditSolution = lazy(() => import("@/pages/admin/EditSolution"));
const AdminEditPlatform = lazy(() => import("@/pages/admin/EditPlatform"));
const AdminEditBlog = lazy(() => import("@/pages/admin/EditBlog"));
const AdminEditPartner = lazy(() => import("@/pages/admin/EditPartner"));
const AdminTagManagement = lazy(() => import("@/pages/admin/TagManagement"));
const AdminSolutionUpdater = lazy(() => import("@/pages/admin/SolutionUpdater"));
const BlogAIAssistant = lazy(() => import("@/pages/admin/BlogAIAssistant"));
const BlogImageGenerator = lazy(() => import("@/pages/admin/BlogImageGenerator"));
const AdminAnalytics = lazy(() => import("@/pages/admin/Analytics"));
const AdminUserManagement = lazy(() => import("@/pages/admin/UserManagement"));
const AdminContactRequests = lazy(() => import("@/pages/admin/ContactRequests"));
const AdminSecurityLogs = lazy(() => import("@/pages/admin/SecurityLogs"));
const AdminBackupManager = lazy(() => import("@/pages/admin/BackupManager"));
const AdminNewsletterGenerator = lazy(() => import("@/pages/admin/NewsletterGenerator"));
const AdminSEOAudit = lazy(() => import("@/pages/admin/SEOAudit"));
const AdminBulkActions = lazy(() => import("@/pages/admin/BulkActions"));
const AdminAPIUsage = lazy(() => import("@/pages/admin/APIUsage"));
const AdminSolutionNews = lazy(() => import("@/pages/admin/SolutionNews"));
const AdminAutoDiscover = lazy(() => import("@/pages/admin/AutoDiscover"));
const AdminScheduledDiscovery = lazy(() => import("@/pages/admin/ScheduledDiscovery"));
const AdminPlatformUpdater = lazy(() => import("@/pages/admin/PlatformUpdater"));
const AdminPowerUpdater = lazy(() => import("@/pages/admin/PowerUpdater"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

const App = () => (
  <ErrorBoundary>
    <LanguageProvider>
      <AuthProvider>
        <CompareProvider>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                {/* Canonical route for AI solutions listing */}
                <Route path="/search" element={<Solutions />} />
                <Route path="/search/:id" element={<SolutionDetail />} />
                {/* 301-style redirects from old URLs */}
                <Route path="/solutions" element={<Navigate to="/search" replace />} />
                <Route path="/solutions/:id" element={<RedirectSolutionDetail />} />
                <Route path="/platforms" element={<Platforms />} />
                <Route path="/platforms/:id" element={<PlatformDetail />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:id" element={<BlogPost />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                {/* Vendor routes */}
                <Route path="/vendor" element={<ProtectedRoute requiredRole="vendor"><VendorDashboard /></ProtectedRoute>} />
                <Route path="/vendor/submit-solution" element={<ProtectedRoute requiredRole="vendor"><SubmitSolution /></ProtectedRoute>} />
                <Route path="/vendor/submit-platform" element={<ProtectedRoute requiredRole="vendor"><SubmitPlatform /></ProtectedRoute>} />
                <Route path="/vendor/edit-solution/:id" element={<ProtectedRoute requiredRole="vendor"><VendorEditSolution /></ProtectedRoute>} />
                <Route path="/vendor/edit-platform/:id" element={<ProtectedRoute requiredRole="vendor"><VendorEditPlatform /></ProtectedRoute>} />
                {/* Admin routes */}
                <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/edit-solution/:id" element={<ProtectedRoute requiredRole="admin"><AdminEditSolution /></ProtectedRoute>} />
                <Route path="/admin/edit-platform/:id" element={<ProtectedRoute requiredRole="admin"><AdminEditPlatform /></ProtectedRoute>} />
                <Route path="/admin/edit-blog/:id" element={<ProtectedRoute requiredRole="admin"><AdminEditBlog /></ProtectedRoute>} />
                <Route path="/admin/create-blog" element={<ProtectedRoute requiredRole="admin"><AdminEditBlog /></ProtectedRoute>} />
                <Route path="/admin/edit-partner/:id" element={<ProtectedRoute requiredRole="admin"><AdminEditPartner /></ProtectedRoute>} />
                <Route path="/admin/create-partner" element={<ProtectedRoute requiredRole="admin"><AdminEditPartner /></ProtectedRoute>} />
                <Route path="/admin/tags" element={<ProtectedRoute requiredRole="admin"><AdminTagManagement /></ProtectedRoute>} />
                <Route path="/admin/solution-updater" element={<ProtectedRoute requiredRole="admin"><AdminSolutionUpdater /></ProtectedRoute>} />
                <Route path="/admin/blog-ai" element={<ProtectedRoute requiredRole="admin"><BlogAIAssistant /></ProtectedRoute>} />
                <Route path="/admin/blog-images" element={<ProtectedRoute requiredRole="admin"><BlogImageGenerator /></ProtectedRoute>} />
                <Route path="/admin/analytics" element={<ProtectedRoute requiredRole="admin"><AdminAnalytics /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute requiredRole="admin"><AdminUserManagement /></ProtectedRoute>} />
                <Route path="/admin/contacts" element={<ProtectedRoute requiredRole="admin"><AdminContactRequests /></ProtectedRoute>} />
                <Route path="/admin/security" element={<ProtectedRoute requiredRole="admin"><AdminSecurityLogs /></ProtectedRoute>} />
                <Route path="/admin/backups" element={<ProtectedRoute requiredRole="admin"><AdminBackupManager /></ProtectedRoute>} />
                <Route path="/admin/newsletter" element={<ProtectedRoute requiredRole="admin"><AdminNewsletterGenerator /></ProtectedRoute>} />
                <Route path="/admin/seo" element={<ProtectedRoute requiredRole="admin"><AdminSEOAudit /></ProtectedRoute>} />
                <Route path="/admin/bulk" element={<ProtectedRoute requiredRole="admin"><AdminBulkActions /></ProtectedRoute>} />
                <Route path="/admin/api-usage" element={<ProtectedRoute requiredRole="admin"><AdminAPIUsage /></ProtectedRoute>} />
                <Route path="/admin/solution-news" element={<ProtectedRoute requiredRole="admin"><AdminSolutionNews /></ProtectedRoute>} />
                <Route path="/admin/auto-discover" element={<ProtectedRoute requiredRole="admin"><AdminAutoDiscover /></ProtectedRoute>} />
                <Route path="/admin/scheduled-discovery" element={<ProtectedRoute requiredRole="admin"><AdminScheduledDiscovery /></ProtectedRoute>} />
                <Route path="/admin/platform-updater" element={<ProtectedRoute requiredRole="admin"><AdminPlatformUpdater /></ProtectedRoute>} />
                <Route path="/admin/power-updater" element={<ProtectedRoute requiredRole="admin"><AdminPowerUpdater /></ProtectedRoute>} />
                {/* 404 route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Layout>
          <CompareBar />
          <Toaster />
        </CompareProvider>
      </AuthProvider>
    </LanguageProvider>
  </ErrorBoundary>
);

export default App;
